<script lang='ts'>
import { reactive, defineComponent, getCurrentInstance, onBeforeUnmount, onMounted, onUnmounted } from '@vue/composition-api';
import draggable from 'vuedraggable';
import { clearSelection } from '@/_core/utils/misc';
import { dragEnded, dragListDestroyed, dragStarted, noteSortableClass } from './dragInProgress';

interface IProps {
    disableVerticalScroll: boolean;
    disableHorizontalScroll: boolean;
    scrollContainerSelector: string;
    dragClass: string;
    fallbackTolerance: number;

    move: (e: any) => boolean;

    scrollTopOffset: number;
}

const AppDraggable = defineComponent({
    name: 'AppDraggable',
    components: {
        draggable,
    },
    props: {
        disableVerticalScroll: Boolean,
        disableHorizontalScroll: Boolean,
        scrollContainerSelector: String,
        dragClass: String,
        move: Function,
        fallbackTolerance: {
            type: Number,
            default: 5,
        },
        scrollTopOffset: Number,
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props: IProps, ctx) {
        const state = reactive({
            draggedElement: null,
            dragScrollContainer: null,
        });

        const clearIntervals = () => {
            clearInterval(moveLeft);
            clearInterval(moveRight);
            clearInterval(moveUp);
            clearInterval(moveDown);
        };

        const treshold = 20;
        const intervalTime = 30;

        const moveLeftFn = () => {
            state.dragScrollContainer.scrollLeft -= treshold;
        };

        const moveRightFn = () => {
            state.dragScrollContainer.scrollLeft += treshold;
        };

        const moveUpFn = () => {
            state.dragScrollContainer.scrollTop -= treshold;
        };

        const moveDownFn = () => {
            state.dragScrollContainer.scrollTop += treshold;
        };

        let moveLeft: any;
        let moveRight: any;
        let moveUp: any;
        let moveDown: any;

        const mouseMoving = (e: MouseEvent) => {
            clearIntervals();

            // HORIZONTAL
            if (!props.disableHorizontalScroll) {
                const { left: dragContainerLeft, right: dragContainerRight } = state.dragScrollContainer.getBoundingClientRect();
                const draggedElementLeft = e.clientX - 60;
                const draggedElementRight = e.clientX + 60;

                if (draggedElementLeft < dragContainerLeft && state.dragScrollContainer.scrollLeft > treshold) {
                    moveLeft = setInterval(moveLeftFn, intervalTime);
                } else if (
                    draggedElementRight > dragContainerRight &&
                    state.dragScrollContainer.scrollLeft < state.dragScrollContainer.scrollWidth - treshold
                ) {
                    moveRight = setInterval(moveRightFn, intervalTime);
                }
            }

            // VERTICAL
            if (!props.disableVerticalScroll) {
                const { top: dragContainerTop, bottom: dragContainerBottom } = state.dragScrollContainer.getBoundingClientRect();
                const { top: draggedElementTop, bottom: draggedElementBottom } = state.draggedElement.getBoundingClientRect();

                let containerTop = dragContainerTop;
                if (props.scrollTopOffset) {
                    containerTop = containerTop + props.scrollTopOffset;
                }

                if (draggedElementTop < containerTop && state.dragScrollContainer.scrollTop > treshold) {
                    moveUp = setInterval(moveUpFn, intervalTime);
                } else if (
                    draggedElementBottom > dragContainerBottom &&
                    state.dragScrollContainer.scrollTop < state.dragScrollContainer.scrollHeight - treshold
                ) {
                    moveDown = setInterval(moveDownFn, intervalTime);
                }
            }
        };

        const addMouseEventListener = () => {
            document.addEventListener('mousemove', mouseMoving);
        };

        const removeMouseEventListener = () => {
            document.removeEventListener('mousemove', mouseMoving);
        };

        const onMoveCallback = (e: any) => {
            // e.preventDefault();
            if (props.scrollContainerSelector == null) {
                state.dragScrollContainer = e.to;
            }

            if (props.move) {
                return props.move(e);
            }

            return true;
        };

        const onDragStart = (e: any) => {
            dragStarted(state, () => {
                removeMouseEventListener();
                clearIntervals();
            });
            clearSelection();
            addMouseEventListener();
            state.draggedElement = document.getElementsByClassName(props.dragClass)[0];

            if (props.scrollContainerSelector == null) {
                state.dragScrollContainer = e.to;
            }

            return false;
        };

        const onDragEnd = () => {
            dragEnded(state);
            removeMouseEventListener();
            clearIntervals();
        };

        const instance = getCurrentInstance() as any;
        onBeforeUnmount(() => {
            removeMouseEventListener();
            clearIntervals();
        });
        // After the inner list is gone: destroying it may have ended a drag running in any list, this one's included.
        onUnmounted(() => {
            dragListDestroyed();
            dragEnded(state);
        });

        onMounted(() => {
            noteSortableClass(instance.$refs.draggable?._sortable?.constructor);
            if (props.scrollContainerSelector != null) {
                state.dragScrollContainer = document.querySelector(props.scrollContainerSelector);
            }
        });

        return {
            state,

            onMoveCallback,
            onDragStart,
            onDragEnd,
        };
    },
});

export default AppDraggable;
</script>

<template>
  <draggable
    ref="draggable"
    v-bind="$attrs"
    :scroll="false"
    :dragoverBubble="false"
    :drag-class="dragClass"
    :fallback-tolerance="fallbackTolerance"
    
    :move="onMoveCallback"
    @start="onDragStart"
    @end="onDragEnd"
    v-on="$listeners"
  >
    <slot />
  </draggable>
</template>

<style lang='scss' scoped></style>